/**
 * Schedule a Cleaning — bulk request body (POST /community/schedule/bulk).
 * Maps UI state (cleaningArea, shifts, date picker) to the community schedule API.
 */

import {
  createEmptyCleaningAreaSelection,
  getBlockFloors,
  getBlockIds,
  getEmployeeDescriptionByCategory,
  getFloorsForBlock,
  getSelectedEmployeeIds,
  isFloorChecked,
} from './cleaningAreaData';
import {
  floorNoFromId,
  getCatalogFloorsForBlock,
} from './cleaningScheduleMappers';

export const CLEANING_SCHEDULE_BULK_ENDPOINT = '/community/schedule/bulk';

export const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/** Default date picker state for Schedule a Cleaning (today, multi-select capable). */
export const createDefaultCleaningDatePickerState = (overrides = {}) => {
  const today = getStartOfToday();
  return {
    mode: 'multiple',
    variant: 'cleaning',
    selectedDates: [today],
    singleDate: today,
    startDate: today,
    endDate: today,
    assignedDates: [],
    ...overrides,
  };
};

const PERSONNEL_CATEGORY_IDS = {
  supervisors: 'facility-team',
  employees: 'emp-and-other',
  housekeeping: 'house-keeping',
  security: 'security',
};

/**
 * @param {Record<string, unknown>} datePickerData
 * @returns {string[]} YYYY-MM-DD
 */
export const enumerateSelectedDates = datePickerData => {
  if (!datePickerData) return [];

  if (
    datePickerData.mode === 'multiple' &&
    Array.isArray(datePickerData.selectedDates) &&
    datePickerData.selectedDates.length > 0
  ) {
    return datePickerData.selectedDates
      .map(date => new Date(date).toISOString().split('T')[0])
      .filter(Boolean)
      .sort();
  }

  if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
    const dates = [];
    const cursor = new Date(datePickerData.startDate);
    const end = new Date(datePickerData.endDate);
    cursor.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    while (cursor <= end) {
      dates.push(cursor.toISOString().split('T')[0]);
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }

  const single = datePickerData.singleDate || datePickerData.startDate;
  if (single) {
    return [new Date(single).toISOString().split('T')[0]];
  }

  return [];
};

const resolveBlocks = siteCatalog => siteCatalog?.blocks || [];

const resolveTasks = siteCatalog => siteCatalog?.tasks || [];

const resolveBlockMeta = (blockId, siteCatalog) =>
  resolveBlocks(siteCatalog).find(block => block.id === blockId) || {
    id: blockId,
    label: blockId,
  };

const resolveFloorsForBlock = (blockId, siteCatalog) => {
  const catalogFloors = getCatalogFloorsForBlock(siteCatalog, blockId);
  if (catalogFloors.length > 0) return catalogFloors;
  return getFloorsForBlock(siteCatalog, blockId);
};

const floorHasSelection = (floorState, taskCatalog) =>
  isFloorChecked(floorState) ||
  resolveTasks(taskCatalog).some(task => floorState?.tasks?.[task.id]);

const buildTaskCodes = (floorState, taskCatalog) =>
  resolveTasks(taskCatalog)
    .filter(task => floorState?.tasks?.[task.id])
    .map(task => task.id);

const blockFloorsContext = (blockId, siteCatalog) => ({
  floors: resolveFloorsForBlock(blockId, siteCatalog),
  tasks: resolveTasks(siteCatalog),
});

const buildPersonnelFloors = (cleaningArea, blockId, siteCatalog) =>
  resolveFloorsForBlock(blockId, siteCatalog).map(floor => {
    const floorState =
      getBlockFloors(cleaningArea, blockId, blockFloorsContext(blockId, siteCatalog))[
        floor.id
      ] || {};
    const floorNo =
      typeof floor.floor_no === 'number'
        ? floor.floor_no
        : floorNoFromId(floor.id) ?? 0;
    return {
      floor_no: floorNo,
      floor_name: floor.label,
      selected: floorHasSelection(floorState, siteCatalog),
      task_codes: buildTaskCodes(floorState, siteCatalog),
      description: floorState.description || '',
    };
  });

const buildSecurityFloorNos = (cleaningArea, blockId, siteCatalog) => {
  const floors = getBlockFloors(
    cleaningArea,
    blockId,
    blockFloorsContext(blockId, siteCatalog)
  );
  return resolveFloorsForBlock(blockId, siteCatalog)
    .map(floor => ({
      floor,
      floorNo:
        typeof floor.floor_no === 'number'
          ? floor.floor_no
          : floorNoFromId(floor.id) ?? 0,
    }))
    .filter(({ floor }) => isFloorChecked(floors[floor.id]))
    .map(({ floorNo }) => floorNo);
};

const buildSupervisorCoverage = cleaningArea => {
  const blockIds = getBlockIds(cleaningArea);
  if (blockIds.length === 0) {
    return {
      coverage_scope: 'site',
      block_ids: [],
      amenity_id: null,
    };
  }
  return {
    coverage_scope: 'blocks',
    block_ids: blockIds,
    amenity_id: null,
  };
};

const buildPersonnelBlocks = (cleaningArea, categoryId, siteCatalog) => {
  const blockIds = getBlockIds(cleaningArea);
  return blockIds.map(blockId => {
    const { label } = resolveBlockMeta(blockId, siteCatalog);
    const base = {
      block_id: blockId,
      block_name: label,
    };

    if (categoryId === 'security') {
      return {
        ...base,
        floor_nos: buildSecurityFloorNos(cleaningArea, blockId, siteCatalog),
      };
    }

    return {
      ...base,
      floors: buildPersonnelFloors(cleaningArea, blockId, siteCatalog),
    };
  });
};

const buildPersonnelRole = (cleaningArea, categoryId, siteCatalog) => {
  const user_ids = getSelectedEmployeeIds(cleaningArea, categoryId);
  const description = getEmployeeDescriptionByCategory(cleaningArea, categoryId);

  if (categoryId === 'facility-team') {
    return {
      user_ids,
      description,
      coverage: buildSupervisorCoverage(cleaningArea),
    };
  }

  return {
    user_ids,
    description,
    blocks: buildPersonnelBlocks(cleaningArea, categoryId, siteCatalog),
  };
};

/**
 * @param {ReturnType<typeof createEmptyCleaningAreaSelection>} cleaningArea
 * @param {object} [siteCatalog]
 */
export const buildPersonnelFromCleaningArea = (cleaningArea, siteCatalog) => {
  const area = cleaningArea || createEmptyCleaningAreaSelection();
  return {
    supervisors: buildPersonnelRole(
      area,
      PERSONNEL_CATEGORY_IDS.supervisors,
      siteCatalog
    ),
    employees: buildPersonnelRole(
      area,
      PERSONNEL_CATEGORY_IDS.employees,
      siteCatalog
    ),
    housekeeping: buildPersonnelRole(
      area,
      PERSONNEL_CATEGORY_IDS.housekeeping,
      siteCatalog
    ),
    security: buildPersonnelRole(
      area,
      PERSONNEL_CATEGORY_IDS.security,
      siteCatalog
    ),
  };
};

/**
 * @param {object} shift
 * @param {number} index 0-based slot index
 */
export const buildShiftSlotPayload = (shift, index, siteCatalog) => {
  const cleaningArea = shift?.cleaningArea || createEmptyCleaningAreaSelection();
  return {
    index,
    shift_start_timestamp: shift?.from_timestamp || '',
    shift_end_timestamp: shift?.to_timestamp || '',
    personnel: buildPersonnelFromCleaningArea(cleaningArea, siteCatalog),
  };
};

/**
 * @param {object} params
 * @param {string} params.siteId
 * @param {object} params.datePickerData
 * @param {object[]} params.shifts
 * @returns {object} Bulk schedule body
 */
export const buildCleaningScheduleBulkPayload = ({
  siteId,
  datePickerData,
  shifts,
  siteCatalog,
}) => {
  const selected_dates = enumerateSelectedDates(datePickerData);
  return {
    site_id: siteId,
    selected_dates,
    generate_tasks: false,
    shifts: (shifts || []).map((shift, index) =>
      buildShiftSlotPayload(shift, index, siteCatalog)
    ),
  };
};
